import { serverUrl } from '@utils/MkEnv'
import { WaitlistConfirmation, WaitlistMatchesFound } from '@account/components/Waitlist/MkWaitlistRequestForm'
import { API, FAILED_TO_CANCEL_RESERVATION } from '@constants'
import type { TripDetailsData } from '@features/account/components/MyTrips/TripDetails/types'
import type { ITripsResponse } from '@account/types'
import type { CancellationPreviewResponse, CancelReservationTechnicalParameters } from '@features/account/components/MyTrips/CancelReservationDialog'

export interface VacationItem {
  id: string
  resortName: string
  checkIn: string
  checkOut: string
  confirmationNumber: string
  suiteType: string
  resortImg?: {
    url: string
    description: string
  }
  resortInformation?: {
    address: {
      street: string
      city: string
      zipCode: string
    }
  }
  stay?: {
    checkIn: string
    checkOut: string
  }
}

export interface Waitlist {
  waitlistRequests: {
    data?: WaitlistItem[]
    message?: {
      title: string
      subtitle: string
    }
  }
  waitlistEligible: boolean
  allowNewRequests: boolean
  waitlistCountLimit: number
  currentWaitlistCount: number
}

export interface WaitlistItem {
  productHubId: string
  resortKey: string
  resortName: string
  stay: {
    checkIn: string
    checkOut: string
  }
  resortInformation: {
    address: {
      street: string
      city: string
      zipCode: string
      phoneNumber?: string
    }
    alerts?: string[]
  }
  status: string
  waitlistId: string
  confirmationNumber: string
  waitlistLevel: string
}

export interface WaitlistItemDetails {
  resortName: string
  resortId: string
  productId: string
  requestedSuiteTypes: string[]
  inventoryOfferingLabels: string[]
  requestedCheckIn: string
  requestedCheckOut: string
  minNights: number
  maxNights: number
  email: string
  waitlistId: string
  waitlistLevel: string
  waitlistConfirmationNumber: string
}

export interface WaitlistResort {
  resortId: string
  resortName: string
  productId: string
  waitlistRatePlanId: number
  waitlistBkgWndwId: number
  waitlistStayRuleId: number
}

export interface SuiteType {
  unitType: string
  detailedSuiteTypes: string[]
}

export interface CreateWaitlistRequest {
  resortId: string
  desiredCheckInDate: string
  desiredCheckOutDate: string
  minLengthOfStay: string
  maxLengthOfStay: string
  emailAddress: string
  phoneNumber: string
  productName: string
  productId: string
  waitlistRatePlanId: number
  waitlistBkgWndwId: number
  waitlistStayRuleId: number
  unitTypes: string[]
  inventoryOfferingLabels: string[]
  owner: {
    firstName: string
    lastName: string
  }
  initial: boolean
}

export interface ModifyWaitlistRequest {
  membershipNumber: string
  oldStatus: string
  minLengthOfStay: string
  maxLengthOfStay: string
  desiredCheckInDate: string
  desiredCheckOutDate: string
  inventoryOfferingLabels: string[]
  unitTypes: string[]
  emailAddress: string
  phoneNumber: string
  owner: {
    firstName: string
    lastName: string
  }
  productName: string
  resortName: string
  waitlistLevel: string
  waitlistStayRuleId: string
}

export interface CancelWaitlistRequest {
  waitlistId: string
  waitlistStatus: string
  owner: {
    firstName: string
    lastName: string
  }
}

export interface FetchTripsParams {
  /** 1-based page index. Translated to BE's 0-based contract internally. */
  pageNum?: number
  pageSize?: number
}

/** Raw BE shape for `GET /mkapi/v1/trips`. `pageNum`/`pageSize`, if echoed, are 0-based. */
interface ITripsBackendResponse extends Omit<ITripsResponse, 'upcomingTripsPagination'> {
  totalUpcomingTrips?: number
  totalPastTrips?: number
  pageNum?: number
  pageSize?: number
}

export const fetchTrips = async (authHeaders: Record<string, string>, params?: FetchTripsParams): Promise<ITripsResponse> => {
  // FE contract is 1-based; reject bad input loudly instead of silently coercing.
  if (params?.pageNum !== undefined && (!Number.isInteger(params.pageNum) || params.pageNum < 1)) {
    throw new Error(`fetchTrips: pageNum must be a 1-based positive integer, received ${params.pageNum}`)
  }
  try {
    const qs = new URLSearchParams()
    if (params?.pageSize !== undefined) {
      qs.set('pageSize', String(params.pageSize))
      qs.set('pageNum', String((params.pageNum ?? 1) - 1))
    }
    const query = qs.toString() ? `?${qs.toString()}` : ''

    const response = await fetch(`${serverUrl}${API}/trips${query}`, {
      method: 'GET',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const bodyText = await response.text()
      let errorMessage: string | undefined

      if (bodyText?.trim()) {
        try {
          const errorData = JSON.parse(bodyText)
          if (errorData?.message) {
            errorMessage = errorData.message
          }
        } catch {
          // response body was not JSON (e.g. HTML error page); ignore
        }
      }

      const error = new Error(errorMessage ?? `HTTP ${response.status}`) as Error & { status: number }
      error.status = response.status
      throw error
    }

    const data: ITripsBackendResponse = await response.json()

    // Prefer BE-echoed pagination fields (forward-compat); fall back to request inputs.
    const totalItems = data.totalUpcomingTrips
    let upcomingTripsPagination: ITripsResponse['upcomingTripsPagination']
    if (totalItems !== undefined && params?.pageSize !== undefined) {
      const finalPageNum = data.pageNum !== undefined ? data.pageNum + 1 : (params.pageNum ?? 1)
      const finalPageSize = data.pageSize ?? params.pageSize
      upcomingTripsPagination = {
        pageNum: finalPageNum,
        pageSize: finalPageSize,
        totalItems,
        totalPages: finalPageSize > 0 ? Math.max(1, Math.ceil(totalItems / finalPageSize)) : 1,
      }
    }

    return { ...data, upcomingTripsPagination }
  } catch (error) {
    console.error('Error fetching upcoming vacations:', error)
    throw error
  }
}

// Preview cancellation details API - returns structured CancellationPreviewResponse
export const previewCancellationDetails = async (
  reservationId: string,
  authHeaders: Record<string, string>,
  optionalHeaders?: Record<string, string>,
): Promise<CancellationPreviewResponse> => {
  try {
    const headers: Record<string, string> = {
      ...authHeaders,
      ...optionalHeaders,
      'Content-Type': 'application/json',
    }

    const response = await fetch(`${serverUrl}${API}/vacations/upcoming/${reservationId}/cancel`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      throw new Error(`Failed to preview cancellation details: ${response.status} ${response.statusText}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error previewing cancellation details:', error)
    throw error
  }
}

export const cancelUpcomingReservation = async (
  reservationId: string,
  reservationItemId: string,
  requestParams: CancelReservationTechnicalParameters,
  authHeaders: Record<string, string>,
  optionalHeaders?: Record<string, string>,
): Promise<boolean> => {
  try {
    const headers: Record<string, string> = {
      ...authHeaders,
      ...optionalHeaders,
      'Content-Type': 'application/json',
    }

    const url = `${serverUrl}${API}/vacations/upcoming/${reservationId}/cancel?reservationItemId=${reservationItemId}`

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
      body: JSON.stringify(requestParams),
    })

    if (!response.ok) {
      let errorMessage

      // Try to get message from response body if available
      try {
        const errorText = await response.text()
        if (errorText?.trim()) {
          const errorData = JSON.parse(errorText)
          if (errorData?.message) {
            errorMessage = errorData.message
          }
        }
      } catch {
        errorMessage = FAILED_TO_CANCEL_RESERVATION
      }

      const error = new Error(errorMessage || FAILED_TO_CANCEL_RESERVATION) as any
      error.status = response.status
      error.response = response
      throw error
    }

    return true
  } catch (error) {
    console.error('Error canceling reservation:', error)
    throw error
  }
}

export const fetchTripDetails = async (confirmationNumber: string, authHeaders: Record<string, string>): Promise<VacationItem> => {
  try {
    const encodedConfirmationNumber = encodeURIComponent(confirmationNumber)
    const response = await fetch(`${serverUrl}${API}/vacations/details/${encodedConfirmationNumber}`, {
      method: 'GET',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch trip details: ${response.status} ${response.statusText}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error fetching trip details:', error)
    throw error
  }
}

// New trip details API - returns structured TripDetailsData
export const fetchTripDetailsData = async (
  confirmationNumber: string,
  authHeaders: Record<string, string>,
  optionalHeaders?: Record<string, string>,
): Promise<TripDetailsData> => {
  try {
    const headers: Record<string, string> = {
      ...authHeaders,
      ...optionalHeaders,
      'Content-Type': 'application/json',
    }

    const encodedConfirmationNumber = encodeURIComponent(confirmationNumber)
    const response = await fetch(`${serverUrl}${API}/trips/${encodedConfirmationNumber}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch trip details: ${response.status} ${response.statusText}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error fetching trip details:', error)
    throw error
  }
}

// Waitlist API calls
export const fetchWaitlistItems = async (authHeaders: Record<string, string>): Promise<Waitlist> => {
  try {
    const response = await fetch(`${serverUrl}${API}/waitlist`, {
      method: 'GET',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch waitlist: ${response.status} ${response.statusText}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error fetching waitlist items:', error)
    throw error
  }
}

export const fetchWaitlistItemDetails = async (waitlistRequestId: string, authHeaders: Record<string, string>): Promise<WaitlistItemDetails> => {
  try {
    const response = await fetch(`${serverUrl}${API}/waitlist/${waitlistRequestId}`, {
      method: 'GET',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch waitlist item details: ${response.status} ${response.statusText}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error fetching waitlist item details:', error)
    throw error
  }
}

export const fetchWaitlistResorts = async (authHeaders: Record<string, string>): Promise<WaitlistResort[]> => {
  try {
    const response = await fetch(`${serverUrl}${API}/waitlist/resorts`, {
      method: 'GET',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch waitlist resorts: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()
    return data.resorts || []
  } catch (error) {
    console.error('Error fetching waitlist resorts:', error)
    throw error
  }
}

export const fetchSuiteTypes = async (resortId: string, authHeaders: Record<string, string>): Promise<SuiteType[]> => {
  try {
    const encodedResortId = encodeURIComponent(resortId)
    const response = await fetch(`${serverUrl}${API}/waitlist/suiteTypes/${encodedResortId}`, {
      method: 'GET',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch suite types: ${response.status} ${response.statusText}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error fetching suite types:', error)
    throw error
  }
}

export const cancelWaitlistRequest = async (cancellationRequest: CancelWaitlistRequest, authHeaders: Record<string, string>): Promise<boolean> => {
  try {
    const response = await fetch(`${serverUrl}${API}/waitlist/${cancellationRequest.waitlistId}`, {
      method: 'DELETE',
      body: JSON.stringify(cancellationRequest),
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      // Try to extract error details from response body
      let errorData = null
      try {
        errorData = await response.json()
      } catch (parseError) {
        // If we can't parse the response, use the status text
        errorData = { message: response.statusText }
      }

      // Create an error object that includes the response and parsed error data
      const error = new Error(`Failed to cancel waitlist request: ${response.status} ${response.statusText}`) as any
      error.response = response
      error.errors = errorData.errors || []
      error.status = response.status
      throw error
    }

    return true
  } catch (error) {
    console.error('Error canceling waitlist request:', error)
    throw error
  }
}

export const createWaitlistRequest = async (waitlistRequest: CreateWaitlistRequest, authHeaders: Record<string, string>): Promise<WaitlistMatchesFound | WaitlistConfirmation> => {
  try {
    const response = await fetch(`${serverUrl}${API}/waitlist`, {
      method: 'POST',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(waitlistRequest),
    })

    if (!response.ok) {
      throw new Error(`Failed to create waitlist request: ${response.status} ${response.statusText}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error creating waitlist request:', error)
    throw error
  }
}

export const modifyWaitlistRequest = async (
  waitlistRequestId: string,
  waitlistRequest: ModifyWaitlistRequest,
  authHeaders: Record<string, string>,
): Promise<WaitlistMatchesFound | WaitlistConfirmation> => {
  try {
    const response = await fetch(`${serverUrl}${API}/waitlist/${waitlistRequestId}`, {
      method: 'PUT',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(waitlistRequest),
    })

    if (!response.ok) {
      // Try to extract error details from response body
      let errorData = null
      try {
        errorData = await response.json()
      } catch (parseError) {
        // If we can't parse the response, use the status text
        errorData = { message: response.statusText }
      }

      // Create an error object that includes the response and parsed error data
      const error = new Error(`Failed to modify waitlist request: ${response.status} ${response.statusText}`) as any
      error.response = response
      error.errors = errorData.errors || []
      error.status = response.status
      throw error
    }

    return await response.json()
  } catch (error) {
    console.error('Error modifying waitlist request:', error)
    throw error
  }
}
